import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, CircularProgress, Container, Grid, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {useApiService} from '../../context/ApiServiceContext';
import {
    GenerationDetail,
    GenerationListItem,
    GenerationRetryResponse,
    GenerationsPage
} from '../../models/Generation';
import {
    GENERATIONS_PAGE_SIZE,
    generationFileName,
    isForbiddenError,
    isGoneError,
    saveUrlAsFile
} from '../../services/generationService';
import CreationCard from './CreationCard';
import CreationLightbox from './CreationLightbox';
import ReportIssueDialog from './ReportIssueDialog';

const ContentContainer = styled(Container)(({theme}) => ({
    padding: theme.spacing(3, 1),
}));

const EmptyState = styled(Box)(({theme}) => ({
    textAlign: 'center',
    padding: theme.spacing(6, 0),
}));

/**
 * "היצירות שלי" — every image and video the user's TEAM has produced.
 *
 * Team-visible with per-row attribution, because a club's graphics are the
 * club's, not one editor's: a colleague can find, download and retry what
 * someone else made, and every card says who made it.
 *
 * Paginated by CURSOR, never page numbers. The list is append-heavy (a new
 * generation lands at the top while the grid is open) and an offset silently
 * repeats or drops rows under exactly that.
 */
const MyCreations: React.FC = () => {
    const {t} = useTranslation();
    const {api} = useApiService();
    const navigate = useNavigate();

    const [items, setItems] = useState<GenerationListItem[]>([]);
    const [cursor, setCursor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    /** The team is not enabled for this feature. Reachable only from a stale
     *  bundle whose route gate predates the flag — the screen says so once and
     *  stops, instead of a toast per retry. */
    const [unavailable, setUnavailable] = useState(false);

    const [selected, setSelected] = useState<GenerationListItem | null>(null);
    const [detail, setDetail] = useState<GenerationDetail | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [reporting, setReporting] = useState(false);

    const fetchPage = useCallback((after: string | null) => {
        const params: Record<string, unknown> = {limit: GENERATIONS_PAGE_SIZE};
        // An empty `after` is a serializer artefact, not "start from the empty
        // cursor" — only send it once there is one.
        if (after) params.after = after;
        return api.get<GenerationsPage>('/generations', {params});
    }, [api]);

    const loadFirstPage = useCallback(() => {
        setLoading(true);
        fetchPage(null)
            .then((response) => {
                setItems(response.data.items ?? []);
                setCursor(response.data.nextCursor ?? null);
            })
            .catch((error) => {
                if (isForbiddenError(error)) {
                    setUnavailable(true);
                    return;
                }
                toast.error(t('main.creations.loadError'));
            })
            .finally(() => setLoading(false));
    }, [fetchPage, t]);

    useEffect(() => {
        loadFirstPage();
    }, [loadFirstPage]);

    const loadMore = () => {
        if (!cursor || loadingMore) return;
        setLoadingMore(true);
        fetchPage(cursor)
            .then((response) => {
                setItems((previous) => [...previous, ...(response.data.items ?? [])]);
                setCursor(response.data.nextCursor ?? null);
            })
            .catch((error) => {
                if (isForbiddenError(error)) {
                    setUnavailable(true);
                    return;
                }
                toast.error(t('main.creations.loadError'));
            })
            .finally(() => setLoadingMore(false));
    };

    /** Patch one row in place — used by the actions that change its state
     *  (an expired download, a filed report) without refetching the page. */
    const patchItem = (id: string, patch: Partial<GenerationListItem>) => {
        setItems((previous) => previous.map((item) => item.id === id ? {...item, ...patch} : item));
        setSelected((previous) => previous && previous.id === id ? {...previous, ...patch} : previous);
    };

    /**
     * Which generation the open pane is FOR. A ref, not state: the promise
     * callbacks below capture their value at call time, so a state read inside
     * them would be the stale one from the render that fired the request.
     *
     * Without this, opening A and then quickly B lets A's slower response paint
     * over B's pane — while every action (download/retry/report) still targets
     * B, because `selected` was set synchronously on the click.
     */
    const openDetailIdRef = useRef<string | null>(null);

    const openItem = (item: GenerationListItem) => {
        openDetailIdRef.current = item.id;
        setSelected(item);
        setDetail(null);
        setLoadingDetail(true);
        api.get<GenerationDetail>(`/generations/${item.id}`)
            .then((response) => {
                if (openDetailIdRef.current !== item.id) return;
                setDetail(response.data);
            })
            .catch(() => {
                if (openDetailIdRef.current !== item.id) return;
                toast.error(t('main.creations.loadError'));
            })
            .finally(() => {
                // Superseded: the newer request owns the spinner now.
                if (openDetailIdRef.current !== item.id) return;
                setLoadingDetail(false);
            });
    };

    const closeItem = () => {
        openDetailIdRef.current = null;
        setSelected(null);
        setDetail(null);
    };

    /**
     * Download through the API, never the presigned URL already on the page:
     * that one is minutes old and its issuance was never recorded. The fresh
     * one is logged server-side, which is the evidence half of an appeal.
     */
    const handleDownload = async () => {
        if (!selected) return;
        setDownloading(true);
        try {
            const response = await api.get<{ url: string }>(`/generations/${selected.id}/url`);
            await saveUrlAsFile(response.data.url, generationFileName(selected));
        } catch (error) {
            if (isGoneError(error)) {
                // Not a failure: the master aged out. Flip the row to expired so
                // the grid stops offering a download that cannot work.
                patchItem(selected.id, {expired: true});
                setDetail((previous) => previous ? {...previous, expired: true} : previous);
                toast.info(t('main.creations.download.expired'));
            } else {
                toast.error(t('main.creations.download.error'));
            }
        } finally {
            setDownloading(false);
        }
    };

    const handleRetry = async () => {
        if (!selected) return;
        setRetrying(true);
        try {
            await api.post<GenerationRetryResponse>(`/generations/${selected.id}/retry`);
            toast.success(t('main.creations.retry.success'));
            closeItem();
            // The retry is a NEW record at the top of the list; the original
            // stays exactly as it happened.
            loadFirstPage();
        } catch (error) {
            toast.error(t(isGoneError(error) ? 'main.creations.retry.expired' : 'main.creations.retry.error'));
        } finally {
            setRetrying(false);
        }
    };

    const handleReport = async (message: string) => {
        if (!selected) return;
        setReporting(true);
        try {
            const response = await api.post<GenerationDetail>(
                `/generations/${selected.id}/report`, {message: message || null});
            setDetail(response.data);
            patchItem(selected.id, {reported: true});
            setReportOpen(false);
            toast.success(t('main.creations.report.success'));
        } catch {
            toast.error(t('main.creations.report.error'));
        } finally {
            setReporting(false);
        }
    };

    return (
        <ContentContainer maxWidth="xl">
            <Box sx={{mb: 2}}>
                <Typography variant="h5" color="white">{t('main.creations.title')}</Typography>
                <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.7)'}}>
                    {t('main.creations.subtitle')}
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress/>
                </Box>
            ) : unavailable ? (
                <EmptyState data-testid="creations-unavailable">
                    <Typography variant="h6" sx={{color: 'rgba(255,255,255,0.7)'}}>
                        {t('main.creations.unavailable')}
                    </Typography>
                </EmptyState>
            ) : items.length === 0 ? (
                <EmptyState>
                    <Typography variant="h6" sx={{color: 'rgba(255,255,255,0.7)'}}>
                        {t('main.creations.empty')}
                    </Typography>
                </EmptyState>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {items.map((item) => (
                            // Two up on a phone: the audience is phone-first and
                            // a full-width card per generation is a lot of scroll.
                            <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                                <CreationCard item={item} onOpen={openItem}/>
                            </Grid>
                        ))}
                    </Grid>

                    {cursor && (
                        <Box sx={{display: 'flex', justifyContent: 'center', pt: 3}}>
                            <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? t('main.creations.loading') : t('main.creations.loadMore')}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {selected && (
                <CreationLightbox
                    item={selected}
                    detail={detail}
                    loadingDetail={loadingDetail}
                    downloading={downloading}
                    retrying={retrying}
                    onClose={closeItem}
                    onDownload={handleDownload}
                    onRetry={handleRetry}
                    onOpenTemplate={() => navigate(`/template/${selected.templateId}`)}
                    onReport={() => setReportOpen(true)}
                />
            )}

            <ReportIssueDialog
                open={reportOpen}
                submitting={reporting}
                onClose={() => setReportOpen(false)}
                onSubmit={handleReport}
            />
        </ContentContainer>
    );
};

export default MyCreations;
